import type {
  AdminAiUsageResponse,
  AdminStatsResponse,
  AdminUsersResponse,
  GrantRequest,
  GrantResponse,
  DeleteResponse,
  ApiError,
} from '../types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

async function fetchApi<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json() as ApiError;
    throw new Error(error.errorMessage || 'API request failed');
  }

  return response.json() as Promise<T>;
}

export async function fetchUsers(
  limit: number = 100,
  offset: number = 0,
): Promise<AdminUsersResponse> {
  return fetchApi<AdminUsersResponse>(
    `/admin/users?limit=${limit}&offset=${offset}`,
  );
}

export async function grantUser(
  userId: string,
  request: GrantRequest,
): Promise<GrantResponse> {
  return fetchApi<GrantResponse>(
    `/admin/users/${userId}/grants`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  );
}

export async function deleteUser(
  userId: string,
): Promise<DeleteResponse> {
  return fetchApi<DeleteResponse>(
    `/admin/users/${userId}`,
    {
      method: 'DELETE',
      headers: {
        'X-Admin-Action-Confirmation': 'DELETE',
      },
    },
  );
}

export async function fetchAiUsage(
  from: string,
  toExclusive: string,
): Promise<AdminAiUsageResponse> {
  const params = new URLSearchParams({
    from,
    to: toExclusive,
    group_by: 'feature',
  });
  return fetchApi<AdminAiUsageResponse>(`/admin/ai/usage?${params.toString()}`);
}

export async function fetchAdminStats(
  from: string,
  toExclusive: string,
): Promise<AdminStatsResponse> {
  const params = new URLSearchParams({
    from,
    to: toExclusive,
  });
  return fetchApi<AdminStatsResponse>(`/admin/stats?${params.toString()}`);
}
